import { useCallback, useEffect, useRef } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useSearchParams } from 'react-router'
import { authenticationState, customerState } from '@repo/unicity'
import { checkoutUuidAtom } from '@/core/checkout/checkout.store'
import { userStatusAtom } from '@/core/auth/user-status.store'
import { getTaiwanFormat, postTaiwanShipping } from '../services/taiwan.api'
import { taiwanFormatDataAtom, taiwanCartSyncingAtom } from '../stores/taiwan.store'
import { buildTaiwanLoginFields } from '../utils/login-fields'

const SYNC_DEBOUNCE_MS = 600

/**
 * Re-sync the server session when the cart is edited from the order summary.
 * A logged-in member PUTs the new `shipment_packs` onto the active uuid, then
 * refetches the format so pricing/terms stay authoritative before payment.
 *
 * No-op when there's no member session/uuid yet (guest cart is authoritative
 * and rebuilt from `cartAtom` at submit time), so it's safe on both the
 * checkout and payment steps.
 */
export const useTaiwanCartSync = () => {
    const [searchParams] = useSearchParams()
    const auth = useAtomValue(authenticationState)
    const customer = useAtomValue(customerState)
    const userStatus = useAtomValue(userStatusAtom)
    const uuid = useAtomValue(checkoutUuidAtom)
    const setFormat = useSetAtom(taiwanFormatDataAtom)
    const setSyncing = useSetAtom(taiwanCartSyncingAtom)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current)
            setSyncing(false)
        },
        [setSyncing]
    )

    // PUT the current cart onto the session + refetch the format. `packsJson`
    // omitted → refetch the format as-is (no cart change). No-op for guests.
    const runSync = useCallback(
        async (packsJson?: string) => {
            const isAuthenticated = !!(auth.token && auth.baId)
            const activeUuid = uuid || searchParams.get('uuid') || ''
            if (!isAuthenticated || !activeUuid) return
            setSyncing(true)
            try {
                if (packsJson !== undefined) {
                    await postTaiwanShipping({
                        type: 'shop',
                        uuid: activeUuid,
                        shipment_packs: packsJson,
                        ...buildTaiwanLoginFields({ auth, customer, userStatus })
                    })
                }
                const format = await getTaiwanFormat(activeUuid)
                setFormat({ ...format, uuid: activeUuid })
            } catch {
                // Best-effort — the client-side summary already reflects the cart.
            } finally {
                setSyncing(false)
            }
        },
        [auth, customer, userStatus, uuid, searchParams, setFormat, setSyncing]
    )

    // Debounced sync for rapid +/- edits — the skeleton shows once the call fires.
    const syncCart = useCallback(
        (packsJson: string) => {
            const isAuthenticated = !!(auth.token && auth.baId)
            const activeUuid = uuid || searchParams.get('uuid') || ''
            if (!isAuthenticated || !activeUuid) return
            if (timerRef.current) clearTimeout(timerRef.current)
            timerRef.current = setTimeout(() => {
                void runSync(packsJson)
            }, SYNC_DEBOUNCE_MS)
        },
        [auth.token, auth.baId, uuid, searchParams, runSync]
    )

    // Immediate update — flushes any pending debounced sync and awaits the PUT +
    // format refetch. Used by "Final step" so the review page has a fresh format.
    const flush = useCallback(
        async (packsJson?: string) => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
            await runSync(packsJson)
        },
        [runSync]
    )

    return { syncCart, flush }
}
